import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Scale } from "lucide-react";

function AuthShell({ title, children }) {
  return (
    <div className="min-h-screen marketing-bg flex flex-col">
      <Link to="/" className="flex items-center gap-2 p-5"><Scale className="w-6 h-6 text-[#0B132B]" /><span className="font-display text-xl font-semibold text-[#0B132B]">NuPDocs</span></Link>
      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-md bg-white border border-[#E2E8F0] rounded-2xl p-8">
          <h1 className="font-display text-3xl font-bold text-[#0B132B] mb-6">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-[#F8F9FA] border border-[#E2E8F0] rounded-lg px-4 py-2.5 text-[#0B132B] focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent";

function GoogleBtn() {
  const go = async () => {
    let clientId = "";
    try { const { data } = await api.get("/auth/google/config"); clientId = data.client_id || ""; } catch (e) { console.debug("Google config unavailable, using hosted fallback:", e); }
    if (!clientId) {
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/auth/callback")}`;
      return;
    }
    const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("g_oauth_state", state);
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: window.location.origin + "/auth/callback",
      response_type: "code",
      scope: "openid email profile",
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };
  return (
    <button onClick={go} data-testid="google-auth-btn" className="w-full border border-[#E2E8F0] rounded-lg py-2.5 font-medium text-[#0B132B] hover:bg-[#F8F9FA] transition-colors flex items-center justify-center gap-2">
      <img src="https://www.google.com/favicon.ico" className="w-4 h-4" alt="" /> Continue with Google
    </button>
  );
}

export function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const { data } = await api.post("/auth/login", { email, password }); setUser(data); nav(data.role === "admin" ? "/admin" : "/dashboard"); }
    catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Welcome back">
      <form onSubmit={submit} className="space-y-4">
        <input className={inputCls} data-testid="login-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input className={inputCls} data-testid="login-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <button disabled={loading} data-testid="login-submit" className="w-full bg-[#0B132B] text-white py-2.5 rounded-lg font-medium hover:bg-[#1C2541] transition-colors">{loading ? "Signing in..." : "Sign In"}</button>
      </form>
      <div className="my-4 text-center text-xs text-[#5C6B73]">or</div>
      <GoogleBtn />
      <p className="mt-6 text-sm text-[#5C6B73] text-center">No account? <Link to="/register" className="text-[#4A6FA5] font-medium">Create one</Link></p>
    </AuthShell>
  );
}

export function Register() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(""); const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const { data } = await api.post("/auth/register", { name, email, password }); setUser(data); nav("/dashboard"); }
    catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Create your account">
      <form onSubmit={submit} className="space-y-4">
        <input className={inputCls} data-testid="register-name" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} required />
        <input className={inputCls} data-testid="register-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input className={inputCls} data-testid="register-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <button disabled={loading} data-testid="register-submit" className="w-full bg-[#0B132B] text-white py-2.5 rounded-lg font-medium hover:bg-[#1C2541] transition-colors">{loading ? "Creating..." : "Create Account"}</button>
      </form>
      <div className="my-4 text-center text-xs text-[#5C6B73]">or</div>
      <GoogleBtn />
      <p className="mt-6 text-sm text-[#5C6B73] text-center">Have an account? <Link to="/login" className="text-[#4A6FA5] font-medium">Sign in</Link></p>
    </AuthShell>
  );
}

export function AuthCallback() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const run = async () => {
      const q = new URLSearchParams(window.location.search);
      const code = q.get("code");
      try {
        let data;
        if (code) {
          const state = q.get("state");
          const saved = sessionStorage.getItem("g_oauth_state");
          sessionStorage.removeItem("g_oauth_state");
          if (!state || state !== saved) { toast.error("Google sign-in failed"); nav("/login"); return; }
          ({ data } = await api.post("/auth/google/session", { code, redirect_uri: window.location.origin + "/auth/callback" }));
        } else {
          const sid = new URLSearchParams(window.location.hash.replace("#", "")).get("session_id");
          if (!sid) { nav("/login"); return; }
          ({ data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } }));
        }
        setUser(data); nav(data.role === "admin" ? "/admin" : "/dashboard");
      }
      catch { toast.error("Google sign-in failed"); nav("/login"); }
    };
    run();
  }, [nav, setUser]);
  return <div className="min-h-screen flex items-center justify-center text-[#5C6B73]">Signing you in…</div>;
}
